import { DeepLink, DeepLinkResolution, DeepLinkType } from "./DeepLink";
import {
  SP_SPDL_TICKET_NUMBER,
  SPDL_TICKET_NUMBER,
} from "../constants/ticketNumberRegex";

/**
 * Pure utility class for URL parsing, ticket extraction, validation,
 * classification, and whitelist-safe redirect calculation.
 *
 * No storage access. No side effects.
 */
export class DeepLinkResolver {
  /**
   * Main entry point. Parses URL, extracts last segment, matches against
   * SP_SPDL_TICKET_NUMBER, classifies type, computes destination (full path),
   * authRedirect (path without ticket), and ticket.
   *
   * @param url The URL pathname to resolve (e.g., "/my-data/history/SPDL-DEV-1234567890ABCD")
   * @returns VALID with DeepLink or INVALID with reason
   */
  static resolve(url: string): DeepLinkResolution {
    const pathname = url || "/";

    const ticket = DeepLinkResolver.getTicketFromPath(pathname);
    const type = DeepLinkResolver.classify(pathname);

    // If the last segment looks like it COULD be a ticket (starts with SP- or SPDL-)
    // but doesn't pass full validation, return INVALID.
    const lastSegment = DeepLinkResolver.getLastSegment(pathname);
    if (
      lastSegment &&
      (lastSegment.startsWith("SP-") || lastSegment.startsWith("SPDL-")) &&
      !ticket
    ) {
      return { status: "INVALID", reason: "INVALID_TICKET" };
    }

    const authRedirect = DeepLinkResolver.getBasePathWithoutTicket(pathname);

    const deepLink: DeepLink = {
      type,
      destination: pathname,
      authRedirect,
      ...(ticket ? { ticket } : {}),
    };

    return { status: "VALID", deepLink };
  }

  /**
   * Extracts the last URL segment and tests it against SP_SPDL_TICKET_NUMBER.
   *
   * @param pathname The URL pathname
   * @returns The ticket string if valid, null otherwise
   */
  static getTicketFromPath(pathname: string): string | null {
    const lastSegment = DeepLinkResolver.getLastSegment(pathname);
    if (!lastSegment) return null;

    if (SP_SPDL_TICKET_NUMBER.test(lastSegment)) {
      return lastSegment;
    }
    return null;
  }

  /**
   * Strips the ticket segment from the URL to produce the whitelist-safe base path.
   * For non-ticket URLs, returns the pathname as-is.
   *
   * @param pathname The URL pathname
   * @returns The base path without the ticket segment
   */
  static getBasePathWithoutTicket(pathname: string): string {
    const ticket = DeepLinkResolver.getTicketFromPath(pathname);
    if (!ticket) return pathname || "/";

    // Remove the ticket segment (and the trailing slash before it)
    const basePath = pathname.replace(`/${ticket}`, "");
    return basePath === "" ? "/" : basePath;
  }

  /**
   * Returns the deeplink classification based on the last path segment.
   *
   * @param pathname The URL pathname
   * @returns "SPDL" if last segment matches SPDL_TICKET_NUMBER,
   *          "SP" if it matches SP_SPDL_TICKET_NUMBER but not SPDL,
   *          "NORMAL" otherwise.
   */
  static classify(pathname: string): DeepLinkType {
    const lastSegment = DeepLinkResolver.getLastSegment(pathname);
    if (!lastSegment) return "NORMAL";

    if (SPDL_TICKET_NUMBER.test(lastSegment)) return "SPDL";
    if (SP_SPDL_TICKET_NUMBER.test(lastSegment)) return "SP";
    return "NORMAL";
  }

  /**
   * Extracts the last non-empty segment from a pathname.
   *
   * @param pathname The URL pathname
   * @returns The last segment, or null if none exists
   */
  private static getLastSegment(pathname: string): string | null {
    if (!pathname) return null;

    const segments = pathname.split("/").filter((s) => s.length > 0);
    if (segments.length === 0) return null;

    return segments[segments.length - 1];
  }
}
